import type { ActivityType } from "../../api/useActivities";
import { buildOptions } from "@ui/Filter";
import * as io from "io-ts";
import t from "./i18n";
import type { Translation } from "../../api/useTranslations";

// Static version for non-React contexts
export const activityLabels = {
  ANALYSIS_ACTIVATION_FILTERS_DELETED: t("selectFilter.analysis_activation_filters_deleted"),
  ANALYSIS_ACTIVATION_FILTERS_REPLACED: t("selectFilter.analysis_activation_filters_replaced"),
  ANALYSIS_ACTIVATION_FREQUENCY_CHANGED: t("selectFilter.analysis_activation_frenquency_changed"),
  ANALYSIS_ACTIVATION_RESET: t("selectFilter.analysis_activation_reset"),
  ANALYSIS_ACTIVATED: t("selectFilter.analysis_activated"),
  ANALYSIS_DEACTIVATED: t("selectFilter.analysis_deactivated"),
  ANALYSIS_PUBLICATION_CHANGED: t("analysis_publication_changed"),
  ANALYSIS_MEASURES_VALIDITY_CHANGED: t("selectFilter.measures_validity_changed"),
  CATEGORY_CHANGED: t("selectFilter.category_changed"),
  CATEGORY_CREATED: t("selectFilter.category_created"),
  CATEGORY_DELETED: t("selectFilter.category_deleted"),
  CROSS_FILTERS_IMPORTED: t("selectFilter.cross_filters_imported"),
  CROSS_FILTERS_DELETED: t("selectFilter.cross_filters_deleted"),
  PROJECT_CATEGORY_CHANGED: t("selectFilter.project_category_changed"),
  PROJECT_CATEGORY_CREATED: t("selectFilter.project_category_created"),
  PROJECT_CATEGORY_DELETED: t("selectFilter.project_category_deleted"),
  COMMENT_CHANGED: t("selectFilter.comment_changed"),
  COMMENT_CREATED: t("selectFilter.comment_created"),
  COMMENT_DELETED: t("selectFilter.comment_deleted"),
  DIAGNOSIS_ENDED: t("selectFilter.diagnosis_ended"),
  FAILURE_REPARABILITY_CHANGED: t("selectFilter.failure.reparability.changed"),
  FAILURE_STATUS_CHANGED: t("selectFilter.failures_status_changed"),
  FORMULA_QUANTITY_CREATED: t("selectFilter.formula_quantity_created"),
  LIVE_DATA_FLOW_MANAGEMENT: t("selectFilter.live_data_flow_management"),
  PROJECT_CHANGED: t("selectFilter.project_changed"),
  PROJECT_CREATED: t("selectFilter.project_created"),
  PROJECT_DELETED: t("selectFilter.project_deleted"),
  PROJECT_DUPLICATED: t("selectFilter.project_duplicated"),
  PROJECT_DUPLICATED_FROM: t("selectFilter.project_duplicated_from"),
  SOURCE_DUPLICATED: t("selectFilter.source_duplicated"),
  PROJECT_MANUAL_FAILURE_METADATA_CHANGED: t(
    "activity.selectFilter.project_manual_failure_metadata.changed",
  ),
  QOIS_CHANGED: t("selectFilter.qois_changed"),
  QUANTITIES_DELETED: t("selectFilter.quantities_deleted"),
  QUANTITIES_CHANGED: t("selectFilter.quantities_changed"),
  QUANTITIES_REPLACED: t("selectFilter.quantities_replaced"),
  SETUP_CREATED: t("selectFilter.setup_created"),
  SETUP_CHANGED: t("selectFilter.setup_changed"),
  SETUP_DELETED: t("selectFilter.setup_deleted"),
  SETUP_DIAGNOSIS_PRESET_CHANGED: t("selectFilter.setup_diagnosis_preset_changed"),
  SETUP_DIAGNOSIS_PARAMETERS_CHANGED: t("selectFilter.setup_diagnosis_parameters_changed"),
  SETUP_DIAGNOSIS_FAILURES_CHANGED: t("selectFilter.setup_diagnosis_diagnosis_failures_changed"),
  SETUP_DIAGNOSIS_FAILURES_DELETED: t("selectFilter.setup_diagnosis_diagnosis_failures_deleted"),
  SETUP_DIAGNOSIS_FAILURES_METADATA_CHANGED: t(
    "selectFilter.setup_diagnosis_failures_metadata_changed",
  ),
  SETUP_DIAGNOSIS_FAILURES_REPLACED: t("selectFilter.setup_diagnosis_failures_replaced"),
  SETUP_DIAGNOSIS_OBSERVABLES_CHANGED: t("selectFilter.setup_diagnosis_observable_changed"),
  SETUP_DIAGNOSIS_OBSERVABLES_DELETED: t("selectFilter.setup_diagnosis_observable_deleted"),
  SETUP_DIAGNOSIS_OBSERVABLES_REPLACED: t("selectFilter.setup_diagnosis_observable_replaced"),
  SETUP_SIMULATION_INPUTS_DELETED: t("selectFilter.setup_simulation_input_deleted"),
  SETUP_SIMULATION_INPUTS_REPLACED: t("selectFilter.setup_simulation_input_replaced"),
  SETUP_SIMULATION_MODEL_DELETED: t("selectFilter.setup_simulation_model_deleted"),
  SETUP_SIMULATION_OUTPUTS_DELETED: t("selectFilter.setup_simulation_output_deleted"),
  SETUP_SIMULATION_OUTPUTS_REPLACED: t("selectFilter.setup_simulation_output_replaced"),
  SETUP_SIMULATION_MODEL_REPLACED: t("selectFilter.setup_simulation_model_replaced"),
  SETUP_SIMULATION_MODEL_STATUS_CHECKED: t("selectFilter.setup_simulation_model_status_checked"),
  SETUP_SIMULATION_OFFSET_CHANGED: t("selectFilter.setup_simulation_offset_changed"),
  SETUP_DUPLICATED: t("selectFilter.setup_duplicated"),
  SIMULATION_ENDED: t("selectFilter.simulation_ended"),
  SNAPSHOT_UPSERTED: t("selectFilter.snapshot_upserted"),
  SNAPSHOTS_DELETED: t("selectFilter.snapshots_deleted"),
  SENSOR_VALUE_DELETED: t("selectFilter.sensor_value_deleted"),
  SOURCE_DELETED: t("selectFilter.source.deleted"),
  SENSOR_QUANTITIES_CREATED: t("selectFilter.sensor_quantities_created"),
  OUTDATED: null,
  ANALYSIS_MANUAL_FAILURE_CHANGED: t("activity.selectFilter.analysis.manual_failure.changed"),
  PROJECT_MANUAL_FAILURE_CREATED: t("activity.selectFilter.project_manual_failure.created"),
  PROJECT_MANUAL_FAILURE_DELETED: t("activity.selectFilter.project_manual_failure.deleted"),
  FAILURE_REINTEGRATED: t("activity.selectFilter.failure_reintegrated"),
  DASHBOARD_CREATED: (translations: Translation) =>
    t("selectFilter.dashboard_created", translations),
  DASHBOARD_CHANGED: (translations: Translation) =>
    t("selectFilter.dashboard_changed", translations),
  DASHBOARD_PUBLICATION_CHANGED: (translations: Translation) =>
    t("selectFilter.dashboard_publication_changed", translations),
  DASHBOARD_DELETED: (translations: Translation) =>
    t("selectFilter.dashboard_deleted", translations),
  DASHBOARD_DUPLICATED: (translations: Translation) =>
    t("selectFilter.dashboard_duplicated", translations),
  FAULT_EVENT_STATUS_CHANGED: t("selectFilter.fault_event_status_changed"),
  FAULT_EVENT_NAME_CHANGED: t("selectFilter.fault_event_name_changed"),
  FAULT_EVENT_PRIORITY_CHANGED: t("selectFilter.fault_event_priority_changed"),
  FAULT_EVENT_DIAGNOSES_STATUS_CHANGED: t("selectFilter.fault_event_diagnoses_status_changed"),
  FAULT_EVENT_DASHBOARD_CREATED: (translations: Translation) =>
    t("selectFilter.fault_event_dashboard_created", translations),
  FAULT_EVENT_DASHBOARD_DELETED: (translations: Translation) =>
    t("selectFilter.fault_event_dashboard_deleted", translations),
  FAULT_EVENT_SPLIT: t("selectFilter.fault_event_split"),
  FAULT_EVENT_MERGE: t("selectFilter.fault_event_merge"),
  MANUAL_FAULT_EVENT_CREATED: t("activity.selectFilter.manual_fault_event.created"),
  MANUAL_FAULT_EVENT_STATUS_UPDATED: t("activity.selectFilter.manual_fault_event.status_updated"),
  MANUAL_FAULT_EVENT_DELETED: t("activity.selectFilter.manual_fault_event.deleted"),
  MANUAL_FAULT_EVENT_DASHBOARD_CREATED: t(
    "activity.selectFilter.manual_fault_event.dashboard_created",
  ),
  MANUAL_FAULT_EVENT_DASHBOARD_DELETED: t(
    "activity.selectFilter.manual_fault_event.dashboard_deleted",
  ),
  MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_CREATED: t(
    "activity.selectFilter.manual_fault_event.time_window_created",
  ),
  MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_DELETED: t(
    "activity.selectFilter.manual_fault_event.time_window_deleted",
  ),
  MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_UPDATED: t(
    "activity.selectFilter.manual_fault_event.time_window_updated",
  ),
  DASHBOARD_COMMENT_CREATED: (translations: Translation) =>
    t("selectFilter.dashboard_comment_created", translations),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} satisfies { [key in ActivityType]: null | string | ((...args: any[]) => string) };

export const activityTypeList = Object.keys(activityLabels) as ActivityType[];
export const ActivityTypeValidator = io.keyof(activityLabels);

export const activityOptionsFilter = (translations: Translation) =>
  buildOptions(
    activityTypeList.flatMap(activityType =>
      activityLabels[activityType]
        ? [
            {
              label:
                typeof activityLabels[activityType] === "function"
                  ? activityLabels[activityType](translations)
                  : activityLabels[activityType]!,
              value: activityType,
            },
          ]
        : [],
    ),
  );
